/**
 * FEATREG (#3711): one registry of features, next to the RBAC config (rbac.ts).
 *
 * Each entry describes a page once: the catalogue route and label, where it
 * sits in the sidebar (`nav`), the grant that gates it, the data it reads, the
 * write actions it owns, the notification events it can raise, and the
 * Analyst tools that already serve its data.
 *
 * Slice 2 (FEATREG2): the page catalogue is GENERATED from this file.
 * sidebar-pages.ts derives SIDEBAR_SECTIONS, ALL_PAGE_PATHS, PATH_ALIASES,
 * ALIAS_REDIRECTS and PAGE_WRITE_ACTIONS from FEATURE_SECTIONS and
 * FEATURE_REGISTRY; the Sidebar, the role editor, the route guard and the
 * server page gate all read those. ORDER IS LOAD-BEARING: entries are grouped
 * by section in FEATURE_SECTIONS order, and the order within a section is the
 * visible nav order. tests/page-catalogue-role-matrix-3711.test.ts holds every
 * role's sidebar rows and gate verdicts against a reviewed fixture, so a slip
 * here shows up as a named role losing or gaining a named route.
 *
 * Later slices generate ALL_AI_TOOLS / TOOL_DEFINITIONS and the event
 * catalogue from the same entries; tests/feature-registry-4279.test.ts
 * cross-checks `tools`, `events`, `apiPrefixes` and `ostendoCapabilities`
 * against those live sources until they are generated too.
 *
 * Field contracts (each one pinned by a test):
 *  - `route`       the page's path, never a retired alias; unique.
 *  - `name`        the sidebar label.
 *  - `nav`         section and nav flags (see FeatureNav). `aliases` are retired
 *                  paths that redirect here and keep their grants working;
 *                  `accessAliases` are live routes that share this page's grant.
 *  - `capability`  the allowed_pages grant that gates the page. Today every
 *                  entry's capability is its own route; PATH_ALIASES maps the
 *                  route to it when they differ, so the gate honours this field.
 *  - `apiPrefixes` the ALL_API_PREFIXES entries whose `page` hint is this route.
 *  - `ostendoCapabilities` capability names from ostendo-capabilities.v2.json
 *                  the page's own routes are registered under (empty where the
 *                  page has no manifest-gated route).
 *  - `reads`       the tables or sources the page shows, as page-docs names them.
 *  - `writes`      the gated write actions a user performs FROM this page (the
 *                  WriteAction the server route enforces via requireWriteAction);
 *                  PAGE_WRITE_ACTIONS is generated from it. Pages that write from
 *                  outside a data-entry surface (KB, mix designs, TDS) keep [].
 *  - `events`      EmailType values this page's data can raise today.
 *  - `tools`       names from TOOL_DEFINITIONS (src/server/lib/sql-tool.ts);
 *                  [] where no Analyst tool reaches the page's sources yet
 *                  (slice 4 adds them).
 */

import type { WriteAction } from "./rbac";

/** Notification event kinds a feature can raise. Mirrors EmailType in src/server/lib/email-subscriptions.ts. */
export type FeatureEvent = "digest" | "anomaly" | "receipt";

export interface FeatureSection {
  key: string;
  label: string;
  /** The whole section renders in the nav for admins only (pages still governable in the role editor). */
  adminOnly?: true;
}

/**
 * Sidebar sections in nav order. Keys are the Sidebar's SECTION_ICONS and
 * MODULE_GROUPS keys (tests/route-catalog-coverage.test.ts pins both).
 */
export const FEATURE_SECTIONS = [
  { key: "overview", label: "Overview" },
  { key: "production", label: "Production" },
  // #1587: Quality follows Gus's Menu.xlsx "General" Order column (14 Aug edit).
  { key: "quality", label: "Quality" },
  { key: "lab", label: "NATA Lab" },
  { key: "rd", label: "R&D" },
  // #738: directly after R&D.
  { key: "projects", label: "Projects" },
  { key: "analytics", label: "Operations" },
  { key: "commercial", label: "Commercial" },
  { key: "tools", label: "Tools" },
  { key: "internal", label: "Work In Progress", adminOnly: true },
] as const satisfies readonly FeatureSection[];

export type FeatureSectionKey = (typeof FEATURE_SECTIONS)[number]["key"];

export interface FeatureNav {
  section: FeatureSectionKey;
  /** Catalogued (role editor governs it) but skipped by the nav. */
  navHidden?: true;
  /**
   * #2545: the nav row renders for admins only, even for a role holding
   * allowed_pages ["*"]. Hides a row, secures nothing: the page's API must be
   * hard-gated too (requireAdminMiddleware in server/index.ts).
   */
  adminOnly?: true;
  /** #2064: nav nesting only; the page renders indented under this same-section page. */
  parent?: string;
  /** A sub-divider label rendered before this page in its section. */
  dividerBefore?: string;
  /**
   * #719: RETIRED paths that now resolve to this page. Each gets a client
   * redirect (ALIAS_REDIRECTS) and alias-aware RBAC (PATH_ALIASES), so a grant
   * on either path counts for both.
   */
  aliases?: readonly string[];
  /** #714: retired path -> this page's tab key, so the redirect lands on its tab. */
  aliasTabs?: Readonly<Record<string, string>>;
  /** Live routes that belong to this page's RBAC unit (not redirected). */
  accessAliases?: readonly string[];
}

export interface FeatureEntry {
  route: string;
  name: string;
  nav: FeatureNav;
  capability: string;
  apiPrefixes: readonly string[];
  ostendoCapabilities: readonly string[];
  reads: readonly string[];
  writes: readonly WriteAction[];
  events: readonly FeatureEvent[];
  tools: readonly string[];
}

const PLANT_SQL = ["run_sql", "list_tables", "get_table_schema"] as const;

export const FEATURE_REGISTRY: readonly FeatureEntry[] = [
  // ───────────────────────────── Overview ─────────────────────────────
  {
    route: "/",
    name: "Overview",
    nav: { section: "overview" },
    capability: "/",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: [
      "production_monitor_batch",
      "production_monitor_batchmaterial",
      "crosbe_sample",
      "crosbe_certificate",
      "qa_production_*sample",
      "raw_material_costs (D1)",
      "material_cost_map (D1)",
    ],
    writes: [],
    events: ["digest"],
    tools: [...PLANT_SQL, "get_cached_summary", "get_recent_failures", "get_material_costs"],
  },

  // ───────────────────────────── Production ─────────────────────────────
  {
    route: "/production",
    name: "Dashboard",
    nav: { section: "production" },
    capability: "/production",
    apiPrefixes: ["/api/production"],
    ostendoCapabilities: [],
    reads: ["production_monitor_batch", "production_monitor_batchmaterial", "production_monitor_material"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "get_production_sequence", "batch_analysis", "get_cached_summary"],
  },
  // #960 slice 2: read-only weigh sheet over released formulations.
  {
    route: "/weigh-out",
    name: "Weigh-Out",
    nav: { section: "production" },
    capability: "/weigh-out",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["production_monitor_product", "production_monitor_formulation"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "resolve_product_identity", "read_recipe_lines"],
  },
  // #714: Yield & Waste (/waste), Purchase Orders (/supply-chain) and Inventory
  // (/inventory) merged into Materials as tabs. /inventory stays this alias:
  // Product Inventory lives at /product-inventory so no bookmark or grant moves.
  {
    route: "/materials",
    name: "Materials",
    nav: {
      section: "production",
      aliases: ["/waste", "/supply-chain", "/inventory"],
      aliasTabs: { "/waste": "waste", "/supply-chain": "purchase-orders", "/inventory": "inventory" },
    },
    capability: "/materials",
    apiPrefixes: ["/api/materials", "/api/waste"],
    ostendoCapabilities: [],
    reads: ["production_monitor_batchmaterial", "production_monitor_batch", "production_monitor_material"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "resolve_material_identity", "aggregate_data"],
  },
  {
    route: "/product-families",
    name: "Product Families",
    nav: { section: "production" },
    capability: "/product-families",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["production_monitor_batch.product_lookup"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "resolve_product_identity"],
  },
  // #715: Changeovers (/changeovers) merged in as a tab. #779: GET-only
  // analytics, so no Data Entry divider in Production.
  {
    route: "/shifts",
    name: "Shifts & Changeovers",
    nav: { section: "production", aliases: ["/changeovers"], aliasTabs: { "/changeovers": "changeovers" } },
    capability: "/shifts",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["production_monitor_batch.production_time"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "trend_analysis"],
  },

  // ───────────────────────────── Quality ─────────────────────────────
  // Gus #1. #1207: one Page, two route-backed views; the unattended QC
  // wallboard needs a URL a plant screen can boot into.
  {
    route: "/production-monitor",
    name: "Live Production View",
    nav: { section: "quality", accessAliases: ["/production-monitor/live"] },
    capability: "/production-monitor",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: [
      "production_monitor_batch",
      "production_monitor_batchmaterial",
      "production_monitor_formulation",
      "production_monitor_product",
      "qa_production_*sample",
      "qa_production_*config",
    ],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "get_production_sequence", "get_spec_limits"],
  },
  // Gus #2. #691 UPI register.
  {
    route: "/qa-upi",
    name: "Batch Release / Approvals",
    nav: { section: "quality" },
    capability: "/qa-upi",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["qa_production_upi", "qa_production_*sample"],
    writes: ["create_upi", "edit_upi", "assign_upi"],
    events: [],
    tools: [...PLANT_SQL],
  },
  // #1759 (Gus 21/08): Test Entry is the "+" button on Finished Goods and the
  // #1751 deep links, not a menu item; the route and its grants stay.
  {
    route: "/qa-entry",
    name: "Finished Goods — Test Entry",
    nav: { section: "quality", navHidden: true },
    capability: "/qa-entry",
    apiPrefixes: ["/api/qa"],
    ostendoCapabilities: [],
    reads: ["qa_production_*sample", "qa_production_*config"],
    writes: ["enter_sample", "edit_sample"],
    events: [],
    tools: [...PLANT_SQL, "get_spec_limits"],
  },
  // Gus #9 rename (#1759): "Finished Goods", landing on Control Charts. #1657:
  // the monitor's analysis/list routes and the folded production-list page
  // redirect to their hub tabs here.
  {
    route: "/quality",
    name: "Finished Goods",
    nav: {
      section: "quality",
      aliases: ["/qc-stats", "/quality-scorecard", "/production-monitor/analysis", "/production-monitor/list", "/production-list"],
      aliasTabs: {
        "/qc-stats": "stats",
        "/quality-scorecard": "scorecard",
        "/production-monitor/analysis": "volume",
        "/production-monitor/list": "production-list",
        "/production-list": "production-list",
      },
    },
    capability: "/quality",
    apiPrefixes: ["/api/quality"],
    ostendoCapabilities: [],
    reads: ["qa_production_{slug}sample", "qa_production_{slug}config"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "get_spec_limits", "get_recent_failures", "count_tests", "calculate_process_stats"],
  },
  // Gus #10. #717: Supplier Quality merged in as a tab.
  {
    route: "/raw-materials",
    name: "Raw Materials Analytics",
    nav: { section: "quality", aliases: ["/supplier-quality"], aliasTabs: { "/supplier-quality": "supplier-quality" } },
    capability: "/raw-materials",
    apiPrefixes: ["/api/raw-materials", "/api/rm-qc"],
    ostendoCapabilities: ["qc.receipts"],
    reads: [
      "raw_materials_opc",
      "raw_materials_cac",
      "raw_materials_csa",
      "raw_materials_plasticizer",
      "raw_materials_thickener",
    ],
    writes: [],
    events: ["receipt"],
    tools: [...PLANT_SQL, "resolve_material_identity", "calculate_process_stats"],
  },
  {
    route: "/raw-materials/field-manager",
    name: "RM-QC Field Manager",
    nav: { section: "quality", navHidden: true },
    capability: "/raw-materials/field-manager",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["rm_material", "rm_test_definition", "rm_material_test", "Ostendo item master (COOP)"],
    writes: ["rm_qc_admin"],
    events: [],
    tools: [...PLANT_SQL, "query_ostendo_coop", "resolve_material_identity"],
  },
  // RMVIEW #3386: the sample records page, reached by material code.
  {
    route: "/rm-materials",
    name: "Raw Material Records",
    nav: { section: "quality", navHidden: true },
    capability: "/rm-materials",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["rm_sample", "rm_result", "rm_material", "rm_material_test"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "resolve_material_identity"],
  },
  // RMGRID #3385: the admin-only category grid linked from the hub.
  {
    route: "/rm-categories",
    name: "Raw Materials",
    nav: { section: "quality", navHidden: true },
    capability: "/rm-categories",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["rm_material", "rm_sample"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL],
  },
  // Gus #5 (#1586 made /reminders the calendar; #779 read-only note stands).
  {
    route: "/reminders",
    name: "Calendar",
    nav: { section: "quality" },
    capability: "/reminders",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["reminders_reminder JOIN django_content_type"],
    writes: [],
    events: [],
    tools: [],
  },
  // #1913 (Gus 27/08): live countdown for in-progress tests, its own Page and
  // RBAC unit. #2064: nested INSIDE Calendar in the nav; route and gate unchanged.
  {
    route: "/test-monitor",
    name: "Test Monitor 24h",
    nav: { section: "quality", parent: "/reminders" },
    capability: "/test-monitor",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["qa_sample_test_sets (D1)", "qa_production_*sample", "test_monitor_removals (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
  // Gus #6: lands on the merged hub's Latest tab via the alias redirect.
  {
    route: "/latest-tests",
    name: "Recent Results",
    nav: { section: "quality" },
    capability: "/latest-tests",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["qa_production_*sample", "qa_production_*config"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "get_recent_failures", "get_spec_limits"],
  },
  // Gus #8. #342: per-test-type checkboxes live here (old /test-types follows).
  {
    route: "/spec-limits",
    name: "Specifications",
    nav: { section: "quality", aliases: ["/test-types"] },
    capability: "/spec-limits",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: [
      "information_schema.columns + qa_production_{slug}config",
      "qa_test_type_flags (D1)",
      "qa_production_bagkg",
      "qa_testing_frequency (D1)",
    ],
    writes: ["edit_spec_config", "edit_test_types"],
    events: [],
    tools: [],
  },
  // #2458: read-only ISO 9001 / CARES clause register; reads a shipped constant.
  {
    route: "/compliance/clauses",
    name: "Clause Register",
    nav: { section: "quality" },
    capability: "/compliance/clauses",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["ISO_9001_CLAUSES, CARES_REQUIREMENTS (src/shared/compliance-clauses.ts)"],
    writes: [],
    events: [],
    tools: [],
  },
  // Meeting: deprecated / not-in-menu pages tucked under "More tools", not
  // deleted; grouping only, access untouched (the exec anomaly card links to /insights).
  {
    route: "/insights",
    name: "Insights",
    nav: { section: "quality", dividerBefore: "More tools" },
    capability: "/insights",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["qa_production_{slug}sample", "qa_production_{slug}config"],
    writes: [],
    events: ["anomaly"],
    tools: [...PLANT_SQL, "get_spec_limits", "calculate_process_stats", "trend_analysis", "distribution"],
  },
  {
    route: "/material-sampling",
    name: "Material Sampling",
    nav: { section: "quality" },
    capability: "/material-sampling",
    apiPrefixes: ["/api/material-sampling"],
    ostendoCapabilities: ["material_sampling.consumption"],
    reads: ["material_state (D1, synced from Ostendo InventoryTransactions)", "ItemBatches × ItemMaster (COOP)", "qa_production_upi + per-product sample tables"],
    writes: [],
    events: [],
    tools: [],
  },

  // ───────────────────────────── NATA Lab ─────────────────────────────
  {
    route: "/lab",
    name: "Dashboard",
    nav: { section: "lab" },
    capability: "/lab",
    apiPrefixes: ["/api/lab"],
    ostendoCapabilities: [],
    reads: [
      "crosbe_sample",
      "crosbe_certificate",
      "crosbe_testrequest",
      "crosbe_customer",
      "crosbe_contactperson",
      "crosbe_equipment",
      "crosbe_equipmentcalibration",
    ],
    writes: [],
    events: [],
    tools: [...PLANT_SQL],
  },
  {
    route: "/certificates",
    name: "Certificates",
    nav: { section: "lab" },
    capability: "/certificates",
    apiPrefixes: ["/api/certificates"],
    ostendoCapabilities: [],
    reads: ["crosbe_certificate", "crosbe_testrequest", "crosbe_sample"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL],
  },
  {
    route: "/calibration",
    name: "Calibration",
    nav: { section: "lab" },
    capability: "/calibration",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["crosbe_equipment", "crosbe_equipmentcalibration"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL],
  },

  // ───────────────────────────── R&D ─────────────────────────────
  {
    route: "/rd/mix-designs",
    name: "Mix Designs",
    nav: { section: "rd" },
    capability: "/rd/mix-designs",
    apiPrefixes: ["/api/rd"],
    ostendoCapabilities: [],
    reads: ["research_mixdesign", "research_ingredient", "research_product", "research_rawmaterial", "research_referencevalue"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL],
  },
  {
    route: "/rd/pipeline",
    name: "R&D Pipeline",
    nav: { section: "rd" },
    capability: "/rd/pipeline",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["research_product", "research_mixdesign", "production_monitor_batch"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL],
  },
  // #1177: TDS Editor. Writes land in D1 (drafts/revisions), not the plant
  // Postgres, so it carries no data-entry write action (as /kb and /rd/mix-designs).
  {
    route: "/tds",
    name: "TDS Editor",
    nav: { section: "rd" },
    capability: "/tds",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: [
      "TDS documents, drafts and revisions (D1)",
      "TDS templates (D1)",
      "production_monitor_formulation via production_monitor_batch",
      "standards behind each claim (QC vs TDS)",
    ],
    writes: [],
    events: [],
    tools: ["get_tds_figures"],
  },
  // #718 (Albert demo): moved here from Production; path unchanged.
  {
    route: "/formulations",
    name: "Official Formulations",
    nav: { section: "rd" },
    capability: "/formulations",
    apiPrefixes: ["/api/formulations"],
    ostendoCapabilities: [
      "formulations.product_list",
      "formulations.materials",
      "formulations.ostendo_items",
      "formulations.compare",
    ],
    reads: ["production_monitor_product", "production_monitor_formulation", "production_monitor_material", "app_change_log"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "get_product_info", "resolve_product_identity"],
  },
  {
    route: "/optimizer",
    name: "Formulation Optimiser",
    nav: { section: "rd" },
    capability: "/optimizer",
    apiPrefixes: [],
    ostendoCapabilities: ["intelligence.formulations"],
    reads: [
      "production_monitor_formulation",
      "production_monitor_formulationmaterial",
      "material_cost_map (D1)",
      "raw_material_costs (D1)",
      "emission_factors (D1)",
    ],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "get_material_costs", "calculate_carbon"],
  },
  // #704: RDTI time capture (Timesheet + Tracker); writes via rdti_log.
  {
    route: "/rdti",
    name: "RDTI Time",
    nav: { section: "rd" },
    capability: "/rdti",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["rdti_entry", "rdti_project", "rdti_task", "rdti_crumb"],
    writes: ["rdti_log"],
    events: [],
    tools: [...PLANT_SQL],
  },

  // ───────────────────────────── Projects ─────────────────────────────
  {
    route: "/projects",
    name: "Projects",
    nav: { section: "projects" },
    capability: "/projects",
    apiPrefixes: ["/api/projects"],
    ostendoCapabilities: [],
    reads: ["projects (D1: timeline, concerns, evidence, membership)"],
    writes: [],
    events: [],
    tools: ["read_projects"],
  },

  // ───────────────────────────── Operations ─────────────────────────────
  {
    route: "/cockpit",
    name: "Operations Traceability",
    nav: { section: "analytics" },
    capability: "/cockpit",
    apiPrefixes: [],
    ostendoCapabilities: ["trace.reverse", "trace.fanout", "trace.recall", "trace.recent_orders"],
    reads: [
      "Ostendo InventoryTransactions (COOP + CROS)",
      "Ostendo AssemblyLines",
      "Ostendo BomLines",
      "qa_production_*sample",
      "qa_production_upi",
    ],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "trace_order", "trace_recall", "trace_material", "query_ostendo_coop", "query_ostendo_cros"],
  },
  // ScheduleIt mirror: production + dispatch timeline. Operations, not
  // Production (Akira 2026-08-09). Bookings create/edit/delete (schedule_edit);
  // a board, not a form, so no Data Entry divider.
  {
    route: "/schedule",
    name: "Schedule",
    nav: { section: "analytics" },
    capability: "/schedule",
    apiPrefixes: ["/api/schedule"],
    ostendoCapabilities: [],
    reads: ["sched_groups / sched_resources / sched_events (D1, mirrored from ScheduleIt)"],
    writes: ["schedule_edit"],
    events: [],
    tools: [],
  },
  // RM reordering planner off the production schedule (Tolu 2026-08-12);
  // rm_planner_edit covers lead-time / safety-stock config and the run→SKU map.
  {
    route: "/rm-planner",
    name: "RM Ordering",
    nav: { section: "analytics" },
    capability: "/rm-planner",
    apiPrefixes: ["/api/rm-planner"],
    ostendoCapabilities: [],
    reads: [
      "Schedule board production runs (D1)",
      "Ostendo inventory, purchase orders and BOMs",
      "rm_planner_config / rm_material_policy / rm_product_map (D1)",
    ],
    writes: ["rm_planner_edit"],
    events: [],
    tools: [],
  },
  // Andrew Farlow's morning stand-up as a page; "Product" keeps it distinct
  // from the Materials Inventory tab (raw-material stock).
  {
    route: "/product-inventory",
    name: "Product Inventory",
    nav: { section: "analytics" },
    capability: "/product-inventory",
    apiPrefixes: ["/api/product-inventory"],
    ostendoCapabilities: [],
    reads: [
      "Ostendo VW_CAI_STOCK_ON_HAND (both companies)",
      "qa_production_upi joined to Ostendo batches by AO number",
      "Ostendo SALESHEADER + SALESLINES (CROS)",
      "Ostendo INVENTORYTRANSFERS + INVENTORYTRANSLINES",
      "sched_events (D1)",
      "Ostendo ITEMMASTER (CROS daily copy)",
    ],
    writes: [],
    events: [],
    tools: ["read_product_inventory"],
  },
  {
    route: "/health",
    name: "Plant Health",
    nav: { section: "analytics" },
    capability: "/health",
    apiPrefixes: [],
    ostendoCapabilities: ["intelligence.health_scorecard"],
    reads: [
      "production_monitor_batch",
      "production_monitor_batchmaterial",
      "crosbe_certificate",
      "crosbe_testrequest",
      "crosbe_sample",
      "raw_material_costs (D1)",
      "material_cost_map (D1)",
    ],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "get_cached_summary", "get_material_costs"],
  },
  // #716: Monthly Summary + Cost Analysis moved here from Production; paths unchanged.
  {
    route: "/monthly-summary",
    name: "Monthly Summary",
    nav: { section: "analytics" },
    capability: "/monthly-summary",
    apiPrefixes: ["/api/monthly-summary"],
    ostendoCapabilities: ["finance.monthly_summary"],
    reads: ["production_monitor_batch", "Ostendo BUDGETDETAIL (CROS)"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "query_ostendo_cros"],
  },
  {
    route: "/costs",
    name: "Cost Analysis",
    nav: { section: "analytics" },
    capability: "/costs",
    apiPrefixes: ["/api/costs"],
    ostendoCapabilities: ["material_costs.read"],
    reads: [
      "production_monitor_batchmaterial",
      "production_monitor_material",
      "production_monitor_batch.weight_kg",
      "raw_material_costs (D1)",
      "material_cost_map (D1)",
    ],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "get_material_costs"],
  },
  // #3693: receipt-line report for a chosen period; the path keeps its original
  // name so existing page grants still reach it.
  {
    route: "/costs/rm-purchases-fy26",
    name: "RM Purchases",
    nav: { section: "analytics" },
    capability: "/costs/rm-purchases-fy26",
    apiPrefixes: [],
    ostendoCapabilities: ["finance.raw_material_purchases"],
    reads: ["Ostendo receipt records (COOP)"],
    writes: [],
    events: [],
    tools: ["query_ostendo_coop"],
  },
  // Albert's Option A: one BOM Suite Page with five route-backed tabs. The
  // accessAliases stay LIVE routes; any historical BOM grant reaches the suite.
  {
    route: "/bom-updates",
    name: "BOM Suite",
    nav: {
      section: "analytics",
      accessAliases: ["/bom-summary", "/bom-explorer", "/bom-masterbatch", "/bom-map", "/bom-inputs"],
    },
    capability: "/bom-updates",
    apiPrefixes: ["/api/bom"],
    ostendoCapabilities: ["bom.review", "bom.links.read", "bom.links.manage", "bom.commercial", "bom.explorer_catalog"],
    reads: ["BOM pending changes, versions and review log (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
  // #380 flush-waste attribution: admin + ceo_ops API gate; page grant rides allowed_pages.
  {
    route: "/flush-waste",
    name: "Flush Waste",
    nav: { section: "analytics" },
    capability: "/flush-waste",
    apiPrefixes: [],
    ostendoCapabilities: ["finance.flush_waste"],
    reads: ["Ostendo InventoryAdjustments + InventoryAdjustLines (COOP)", "Ostendo AssemblyLines (COOP)", "Ostendo AssemblyHeader (COOP)"],
    writes: [],
    events: [],
    tools: ["query_ostendo_coop"],
  },
  // Composite surface: re-reads Overview and Cost Analysis plus the COOP journal.
  {
    route: "/report",
    name: "Executive Report",
    nav: { section: "analytics" },
    capability: "/report",
    apiPrefixes: [],
    ostendoCapabilities: ["finance.flush_waste"],
    reads: ["Overview sources", "Cost Analysis sources", "Ostendo JournalHeader + JournalLines (COOP)"],
    writes: [],
    events: [],
    tools: [],
  },

  // ───────────────────────────── Commercial ─────────────────────────────
  // #370 (Akira): under the Commercial umbrella; renamed "Daily Dashboard" 22 Jul (Albert, via Jez).
  {
    route: "/commercial/deliveries",
    name: "Daily Dashboard",
    nav: { section: "commercial" },
    capability: "/commercial/deliveries",
    apiPrefixes: [],
    ostendoCapabilities: ["commercial.deliveries"],
    reads: ["Ostendo SalesInvoiceHeader + SalesInvoiceLines (CROS)"],
    writes: [],
    events: [],
    tools: ["query_ostendo_cros"],
  },
  // #2159 Sales Pipeline: Sales Manager + Sales Rep only (Akira, 8 Sep 2026).
  {
    route: "/commercial/pipeline",
    name: "Sales Pipeline",
    nav: { section: "commercial" },
    capability: "/commercial/pipeline",
    apiPrefixes: [],
    ostendoCapabilities: ["commercial.pipeline", "commercial.customers"],
    reads: ["commercial pipeline entries (D1)", "active customer accounts (CROS)"],
    writes: [],
    events: [],
    tools: [],
  },
  // COMPPRICE #3571: Sales Manager + Sales Rep, and ceo_ops (COMPPRICE-AUD, 29 Sep 2026); not tech_services.
  {
    route: "/commercial/competitor-prices",
    name: "Competitor Prices",
    nav: { section: "commercial" },
    capability: "/commercial/competitor-prices",
    apiPrefixes: [],
    ostendoCapabilities: ["commercial.competitor_prices"],
    reads: ["competitor price entries (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
  // #593 TDSVIEW: every signed-in role views and downloads a product's issued
  // TDS here (a UNIVERSAL_PAGES entry, so it shows for every role). Under
  // Commercial because sales ask for it most (Nathan, Adam). Its API,
  // /api/tds-sheets, is non-grantable (NON_GRANTABLE_API_PREFIXES).
  {
    route: "/data-sheets",
    name: "Data Sheets",
    nav: { section: "commercial" },
    capability: "/data-sheets",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["TDS publication heads issued in the app (D1)", "crosbe.com TDS publication registry (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
  {
    route: "/market",
    name: "Market Intelligence",
    nav: { section: "commercial" },
    capability: "/market",
    apiPrefixes: ["/api/market"],
    ostendoCapabilities: [],
    reads: ["ABS EWD dataflow", "ABS BA_SA2 dataflow", "ABS PPI dataflow (8102192 cement, 8102194 sand)"],
    writes: [],
    events: [],
    tools: [],
  },
  {
    route: "/commercial/customers",
    name: "Customer List",
    nav: { section: "commercial" },
    capability: "/commercial/customers",
    apiPrefixes: [],
    ostendoCapabilities: ["commercial.customers"],
    reads: ["Ostendo customer master (CROS)"],
    writes: [],
    events: [],
    tools: ["query_ostendo_cros", "risk_accounts", "business_lookup"],
  },
  // #953 (Akira-approved trio, build 1 of 3): API is admin + ceo_ops only.
  {
    route: "/commercial/aged-debtors",
    name: "Aged Debtors",
    nav: { section: "commercial" },
    capability: "/commercial/aged-debtors",
    apiPrefixes: [],
    ostendoCapabilities: ["commercial.aged_debtors"],
    reads: ["Ostendo CustomerAgingDetail (CROS)", "Ostendo customer and invoice records (CROS)"],
    writes: [],
    events: [],
    tools: ["query_ostendo_cros", "read_aged_debtors"],
  },
  // AGEDREMIND #1197: schedule, low-credit amount and wording for payment
  // reminders, with a dry-run preview. Sending is off until the captain approves go-live.
  {
    route: "/commercial/aged-debtors-reminders",
    name: "Debtor Reminders",
    nav: { section: "commercial" },
    capability: "/commercial/aged-debtors-reminders",
    apiPrefixes: [],
    ostendoCapabilities: ["commercial.aged_debtors_reminders", "commercial.aged_debtors"],
    reads: ["Aged Debtors figures (CROS)", "Ostendo customer email and salesperson (CROS)", "reminder settings (D1)"],
    writes: [],
    events: [],
    tools: ["read_debtor_reminders", "read_aged_debtors"],
  },
  // #2545 QCTDS: under Commercial at Akira's direction (14 Sep), built for Tech
  // Services. The API also needs the view_qc_vs_tds action (requireWriteAction).
  {
    route: "/commercial/qc-vs-tds",
    name: "QC vs TDS",
    nav: { section: "commercial" },
    capability: "/commercial/qc-vs-tds",
    apiPrefixes: ["/api/qc-vs-tds"],
    ostendoCapabilities: [],
    reads: ["plant QC records", "TDS figures and revisions", "R&D trial results (mix designs)", "per-product QC settings", "claim standards and methods"],
    writes: [],
    events: [],
    tools: ["get_qc_tds_claims"],
  },
  // #587 Troubleshooting Tool (Albert's mockup): guided/AI-chat flow + Technical Services review queue.
  {
    route: "/commercial/troubleshooting",
    name: "Troubleshooting",
    nav: { section: "commercial" },
    capability: "/commercial/troubleshooting",
    apiPrefixes: ["/api/troubleshooting"],
    ostendoCapabilities: [],
    reads: ["troubleshooting questions, causes and actions (D1)", "AI chat with product context"],
    writes: [],
    events: [],
    tools: [],
  },
  // #2056 Cases (Tech Services + Commercial CRM). #2059: the visit form is a
  // live route under the same grant. #4460 TSRENAME: the nav label is "Tech
  // Services" (the case list is its Cases tab); the route, capability and
  // grants stay on /commercial/cases, so no role's access moves.
  {
    route: "/commercial/cases",
    name: "Tech Services",
    nav: { section: "commercial", accessAliases: ["/commercial/cases/visits/new"] },
    capability: "/commercial/cases",
    apiPrefixes: ["/api/cases", "/api/qr", "/api/ts-records"],
    ostendoCapabilities: [],
    reads: ["cases, sites, visits, actions (D1)", "accounts (CROS customers synced nightly + prospects, D1)"],
    writes: ["case_open", "visit_log", "case_manage"],
    events: [],
    tools: ["read_cases", "get_presentation_feedback"],
  },

  // ───────────────────────────── Tools ─────────────────────────────
  {
    route: "/analyst",
    name: "Data Analyst",
    nav: { section: "tools" },
    capability: "/analyst",
    apiPrefixes: ["/api/chat"],
    ostendoCapabilities: [],
    reads: ["all plant Postgres tables (via run_sql)", "raw_material_costs, material_cost_map (D1)", "emission_factors (D1)", "kb_articles (D1)", "products (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
  {
    route: "/digests",
    name: "Digests",
    nav: { section: "tools" },
    capability: "/digests",
    apiPrefixes: ["/api/digests"],
    ostendoCapabilities: [],
    reads: ["production, materials and lab metrics (plant Postgres)", "AI narrative"],
    writes: [],
    events: [],
    tools: [],
  },
  {
    route: "/artifacts",
    name: "Gadgets",
    nav: { section: "tools" },
    capability: "/artifacts",
    apiPrefixes: ["/api/artifacts"],
    ostendoCapabilities: [],
    reads: ["artifacts (D1, scoped to user_id)"],
    writes: [],
    events: [],
    tools: [],
  },
  {
    route: "/kb",
    name: "Knowledge Base",
    nav: { section: "tools" },
    capability: "/kb",
    apiPrefixes: ["/api/kb"],
    ostendoCapabilities: [],
    reads: ["kb_articles (D1)", "kb_misses (D1)"],
    writes: [],
    events: [],
    tools: ["search_kb"],
  },
  {
    route: "/explore",
    name: "Data Explorer",
    nav: { section: "tools" },
    capability: "/explore",
    apiPrefixes: ["/api/data"],
    ostendoCapabilities: [],
    reads: ["all plant Postgres tables (read-only)"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "read_data"],
  },
  {
    route: "/carbon-exec",
    name: "Carbon (Exec)",
    nav: { section: "tools" },
    capability: "/carbon-exec",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["per-product CO2e (formulation BOM x emission_factors)"],
    writes: [],
    events: [],
    tools: ["calculate_carbon"],
  },
  // #2894 (Akira, 18 Sep, via Hugo): admin-only nav row; API hard-gated too
  // (requireAdminMiddleware on /api/portal/admin/*).
  {
    route: "/portal-admin",
    name: "Customer Portal",
    nav: { section: "tools", adminOnly: true },
    capability: "/portal-admin",
    apiPrefixes: ["/api/portal"],
    ostendoCapabilities: [],
    reads: ["portal tokens and customer scopes (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
  {
    route: "/admin",
    name: "User Management",
    nav: { section: "tools" },
    capability: "/admin",
    apiPrefixes: ["/api/admin", "/api/digest-recipients", "/api/rbac", "/api/access-requests", "/api/email-subscriptions", "/api/ostendo-live", "/api/station-admin", "/api/help-inbox", "/api/team-facts"],
    ostendoCapabilities: [],
    reads: ["users, roles and permissions (D1)", "sync run log (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
  // #867 Phase A: impact-ranked unresolved lookup codes + confirm flow.
  {
    route: "/admin/material-mapping",
    name: "Material Mapping",
    nav: { section: "tools" },
    capability: "/admin/material-mapping",
    apiPrefixes: [],
    ostendoCapabilities: ["admin.material_mapping"],
    reads: ["material mappings (D1)", "plant lookup codes and Ostendo items"],
    writes: [],
    events: [],
    tools: [],
  },
  // #922: roles × Ostendo-resources matrix (the #876 granular grants UI).
  {
    route: "/admin/ostendo-access",
    name: "Ostendo Access",
    nav: { section: "tools" },
    capability: "/admin/ostendo-access",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["role grants and resource catalog (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
  {
    route: "/updates",
    name: "Updates",
    nav: { section: "tools" },
    capability: "/updates",
    apiPrefixes: ["/api/updates"],
    ostendoCapabilities: [],
    reads: ["changelog entries and idea cards (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
  {
    route: "/guide",
    name: "User Guide",
    nav: { section: "tools" },
    capability: "/guide",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["guide content (shipped with the app)"],
    writes: [],
    events: [],
    tools: [],
  },
  {
    route: "/settings",
    name: "Settings",
    nav: { section: "tools" },
    capability: "/settings",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["own profile and preferences (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
  // navHidden: routed + role-governable, intentionally not in the visible nav.
  {
    route: "/display",
    name: "Display Mode",
    nav: { section: "tools", navHidden: true },
    capability: "/display",
    apiPrefixes: [],
    ostendoCapabilities: ["intelligence.health_scorecard", "material_costs.read"],
    reads: ["Overview, Cost Analysis and Shift Analysis sources"],
    writes: [],
    events: [],
    tools: [],
  },
  {
    route: "/calendar",
    name: "Production Calendar",
    nav: { section: "tools", navHidden: true },
    capability: "/calendar",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["production_monitor_batch"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL],
  },

  // ───────────────────────────── Work In Progress ─────────────────────────────
  {
    route: "/material-network",
    name: "Material Network",
    nav: { section: "internal" },
    capability: "/material-network",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["production_monitor_batchmaterial"],
    writes: [],
    events: [],
    tools: [...PLANT_SQL],
  },
  // #2894: row-level adminOnly on top of the section's; API hard-gated too
  // (requireAdminMiddleware on /api/intelligence/*).
  {
    route: "/intelligence",
    name: "Plant Intelligence",
    nav: { section: "internal", adminOnly: true },
    capability: "/intelligence",
    apiPrefixes: ["/api/intelligence"],
    ostendoCapabilities: [],
    reads: [
      "qa_production_{slug}sample",
      "production_monitor_batchmaterial",
      "crosbe_customer",
      "crosbe_contactperson",
      "crosbe_sample",
    ],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "calculate_process_stats"],
  },
  {
    route: "/carbon",
    name: "Carbon Footprint",
    nav: { section: "internal" },
    capability: "/carbon",
    apiPrefixes: ["/api/carbon"],
    ostendoCapabilities: [],
    reads: [
      "production_monitor_formulation",
      "production_monitor_formulationmaterial",
      "production_monitor_material",
      "emission_factors (D1)",
    ],
    writes: [],
    events: [],
    tools: [...PLANT_SQL, "calculate_carbon"],
  },
  // #2894: explicit row-level adminOnly; API hard-gated too
  // (requireAdminMiddleware on /api/admin/ai-feedback[/*]).
  {
    route: "/admin/ai-feedback",
    name: "AI Feedback",
    nav: { section: "internal", adminOnly: true },
    capability: "/admin/ai-feedback",
    apiPrefixes: [],
    ostendoCapabilities: [],
    reads: ["AI ratings and flagged conversations (D1)", "open access requests (D1)"],
    writes: [],
    events: [],
    tools: [],
  },
];
